import React, { useState, useRef } from "react";
import { Control, DeepMap, FieldError, Controller, RegisterOptions } from "react-hook-form";
import { Button, FormGroup, FormField, Label } from "semantic-ui-react";
import { fieldError } from "../../../../../utils/functions";
import classNames from "classnames";

interface IControlledChipRadioSelect {
  control: Control;
  name: string;
  options: { value: string | null; text: string }[];
  errors: DeepMap<Record<string, any>, FieldError>;
  rules?: RegisterOptions;
}
export default function ControlledChipRadioSelect({
  control,
  name,
  options,
  errors,
  rules = {},
}: IControlledChipRadioSelect) {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabsRef = useRef<(Button | null)[]>([]);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const newIndex = getNewTabIndex(event, index);
      setActiveIndex(newIndex);
      tabsRef.current[newIndex]?.focus();
    }
  };

  const getNewTabIndex = (event: React.KeyboardEvent, index: number): number => {
    if (event.key === "ArrowRight") {
      return index === options.length - 1 ? 0 : index + 1;
    }
    if (event.key === "ArrowLeft") {
      return index === 0 ? options.length - 1 : index - 1;
    }
    return -1;
  };

  const error = fieldError(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ onChange, value }) => {
        return (
          <div role='tablist' className='tab-list' aria-label='Meridiem Preference'>
            <FormGroup className={`tw-flex tw-justify-evenly md:tw-mt-8 lg:tw-mt-8`} inline>
              {options.map((option, index) => {
                return (
                  <FormField key={option.value} error={!!error}>
                    <Button
                      as='button'
                      role='tab'
                      circular
                      className={classNames(
                        "tw-px-4 focus:tw-border-r-0 focus:tw-ring-2 focus:tw-ring-blue focus:tw-ring-offset-2",
                        {
                          "tw-border-[#E0B4B4] tw-bg-[#FFF6F6] tw-text-[#9f3a38]": !!error,
                          "tw-bg-[#007ac61a] tw-text-[#007AC6]": option.value === value,
                          "tw-bg-transparent": option.value !== value && !error,
                        },
                      )}
                      aria-selected={activeIndex === index ? "true" : "false"}
                      tabIndex={activeIndex === index ? 0 : -1}
                      onClick={() => {
                        if (value === option.value) {
                          onChange(undefined);
                        } else {
                          onChange(option.value);
                        }
                        handleTabClick(index);
                      }}
                      // I don't like what I did here, but it does work... - Peter
                      onKeyDown={(e: unknown) => handleKeyDown(e as any, index)}
                      // Used for managing focus when using arrow keys
                      ref={(el) => {
                        tabsRef.current[index] = el;
                      }}
                    >
                      {option.text}
                    </Button>
                  </FormField>
                );
              })}
            </FormGroup>
            {error && (
              <Label
                basic
                prompt
                pointing='above'
                className={`tw-flex tw-grow ${!!error ? "tw-border-[#E0B4B4] tw-text-[#9f3a38]" : ""}`}
              >
                {error?.content.message}
              </Label>
            )}
          </div>
        );
      }}
    />
  );
}
